import { useState } from 'react';

export interface FAQItem {
  q: string;
  a: string;
}

interface SharedFAQProps {
  faqs: FAQItem[];
  heading?: string;
}

export default function SharedFAQ({ faqs, heading = 'Frequently Asked' }: SharedFAQProps) {
  const [open, setOpen] = useState<number | null>(null);

  return (
    <section className="bg-warm-cream py-24">
      <div className="max-w-[900px] mx-auto px-6">
        <div className="text-center mb-14">
          <span className="font-sans text-charcoal-500 text-xs tracking-[0.3em] uppercase">Common Questions</span>
          <h2 className="font-serif text-charcoal-900 text-4xl md:text-5xl mt-3">{heading}</h2>
          <div className="w-16 h-0.5 bg-gold mx-auto mt-4" />
        </div>

        <div className="space-y-3">
          {faqs.map((faq, i) => (
            <div
              key={i}
              className={`rounded-xl overflow-hidden border transition-colors duration-200 ${
                open === i ? 'border-gold/40 bg-white' : 'border-charcoal-100 bg-white hover:border-gold/20'
              }`}
            >
              <button
                className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left cursor-pointer"
                onClick={() => setOpen(open === i ? null : i)}
                aria-expanded={open === i}
              >
                <span className="font-serif text-charcoal-900 text-base leading-snug pr-4">{faq.q}</span>
                <div className="w-7 h-7 flex items-center justify-center shrink-0 bg-gold/10 rounded-full">
                  <i
                    className={`${open === i ? 'ri-subtract-line' : 'ri-add-line'} text-gold`}
                    style={{ fontSize: '16px' }}
                  />
                </div>
              </button>
              {open === i && (
                <div className="px-6 pb-5">
                  <div className="w-full h-px bg-gold/10 mb-4" />
                  <p className="font-sans text-charcoal-600 text-sm leading-relaxed">{faq.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: faqs.map((faq) => ({
                '@type': 'Question',
                name: faq.q,
                acceptedAnswer: { '@type': 'Answer', text: faq.a },
              })),
            }),
          }}
        />
      </div>
    </section>
  );
}
